/* eslint-disable react/jsx-props-no-spreading, jsx-a11y/label-has-associated-control */
// Spreading react-hook-form's register() result is the library's intended usage. Every
// label is tied to its control by htmlFor/id; airbnb's config also demands nesting,
// which doesn't fit the label-above-field layout (same call as Checkout.js).
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { formatCurrency } from '../../helpers/convertToCurrency';

const MAX_QUANTITY_DIGITS = 2;
const MAX_PRICE_DIGITS = 7;

// Digits and a single decimal point, at most two places. Commas are dropped here and
// put back on blur, so editing a formatted value doesn't fight the cursor.
export const sanitizePrice = value => {
  const [whole = '', ...rest] = String(value)
    .replaceAll(/[^\d.]/g, '')
    .split('.');
  const integer = whole.slice(0, MAX_PRICE_DIGITS);
  return rest.length ? `${integer}.${rest.join('').slice(0, 2)}` : integer;
};

export const parsePrice = value => Number.parseFloat(String(value).replaceAll(',', '')) || 0;

// A number as the money inputs display it - "1,129.99" - or empty for nothing.
const formatAmount = amount => {
  if (amount <= 0) return '';
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPriceInput = value => formatAmount(parsePrice(value)) || value;

const sanitizeQuantity = value => String(value).replaceAll(/\D/g, '').slice(0, MAX_QUANTITY_DIGITS);

const parseQuantity = value => Number.parseInt(value, 10) || 0;

// Text fields allow letters, digits, spaces and the punctuation real product names use
// ("iPhone XS-256GB", 65" TV, "Queen (60/80)"). Symbols such as @ # $ % ^ & are rejected.
const NAME_CHARACTERS = /^[A-Za-z0-9 .,'"\-/()+]*$/;
const NAME_CHARACTERS_HINT = 'Use only letters, numbers, spaces and . , \' " - / ( ) +';
// SKUs are codes: letters, digits and - _ . / only, no spaces.
const SKU_CHARACTERS = /^[A-Za-z0-9\-_./]*$/;
const SKU_CHARACTERS_HINT = 'Use only letters, numbers and - _ . /';

// Validators for a descriptive text field, run in order so the shopper sees one message
// at a time. Minimum lengths and a numbers-only check were split out of this ticket,
// so any non-empty value made of allowed characters is accepted for now.
const nameRules = ({ required }) => ({
  required: value => value.trim().length > 0 || required,
  characters: value => NAME_CHARACTERS.test(value.trim()) || NAME_CHARACTERS_HINT,
});

const toCents = amount => Math.round(amount * 100);

const roundToCents = amount => toCents(amount) / 100;

// What one line costs against the approval. A warranty is bought per unit, as in the
// tele-sales agent cart.
const getLineTotal = (price, quantity, warrantyPrice = 0) => {
  return (parsePrice(price) + parsePrice(warrantyPrice)) * parseQuantity(quantity);
};

// Turns a stored cart item back into the strings the inputs hold. `warranty` was a bare
// boolean before warranties had details, which may still be in persisted state.
const toFormValues = item => {
  const quantity = item?.quantity ? String(item.quantity) : '1';
  const price = item?.price ? formatPriceInput(String(item.price)) : '';
  const warranty = typeof item?.warranty === 'object' ? item.warranty : null;
  return {
    makeModel: item?.makeModel || '',
    description: item?.description || '',
    quantity,
    price,
    total: formatAmount(parsePrice(price) * parseQuantity(quantity)),
    warranty: Boolean(item?.warranty),
    warrantyName: warranty?.name || '',
    warrantySku: warranty?.sku || '',
    warrantyPrice: warranty?.price ? formatPriceInput(String(warranty.price)) : '',
  };
};

function MoneyInput({ field, id, error, onChange, onBlur }) {
  return (
    <span className="k-item-form__money">
      <span className="k-item-form__currency" aria-hidden="true">
        $
      </span>
      <input
        {...field}
        id={id}
        className="k-item-form__input k-item-form__input--money"
        type="text"
        inputMode="decimal"
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      />
    </span>
  );
}

/**
 * Inline form for typing an in-store cart item in off the floor tag, checked against
 * the shopper's remaining approval. Used both to add a new item ("Type it in") and to
 * edit one already in the cart - pass `initialValues` for the latter.
 * Figma: In-Store VCC Flows (NFM) - node 574:834. The warranty fields follow the
 * tele-sales agent cart (Cart.js there), which has no in-store design yet.
 *
 * When editing, `remaining` must already include the item's own current total, since
 * saving replaces it rather than adding to it.
 */
export default function ManualItemForm({ remaining, initialValues, title, submitLabel, onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setFocus,
    trigger,
    formState: { errors, isValid, touchedFields, isSubmitted },
  } = useForm({
    mode: 'onTouched',
    defaultValues: toFormValues(initialValues),
  });

  // Move focus into the form as it opens, since it replaces the button that opened it.
  useEffect(() => {
    setFocus('makeModel');
  }, [setFocus]);

  const hasWarranty = watch('warranty');

  // Errors stay quiet until the shopper has left a field, so the form doesn't open red.
  const errorFor = name => (touchedFields[name] || isSubmitted) && errors[name]?.message;

  // Item price and Total price are two views of the same number: editing either one
  // rewrites the other. Price is the stored value; Total only ever derives it.
  const syncTotalFromPrice = () => {
    const { price, quantity } = getValues();
    setValue('total', formatAmount(roundToCents(parsePrice(price) * parseQuantity(quantity))));
  };

  const syncPriceFromTotal = total => {
    const quantity = parseQuantity(getValues('quantity'));
    if (quantity < 1) return; // Nothing to divide by yet; quantity shows its own error.
    setValue('price', formatAmount(roundToCents(parsePrice(total) / quantity)), {
      shouldValidate: true,
      // So an over-limit total surfaces its error under Item price straight away.
      shouldTouch: true,
    });
  };

  const makeModelField = register('makeModel', {
    validate: nameRules({ required: 'Enter the make or model' }),
  });
  const descriptionField = register('description', {
    validate: nameRules({ required: 'Enter a description' }),
  });
  const quantityField = register('quantity', {
    validate: value => parseQuantity(value) >= 1 || 'Enter at least 1',
    // The over-limit check on price depends on quantity, so re-run it on change.
    deps: ['price'],
  });
  const priceField = register('price', {
    validate: {
      positive: value => parsePrice(value) > 0 || 'Enter a price',
      withinLimit: (value, values) => {
        const warrantyPrice = values.warranty ? values.warrantyPrice : 0;
        const fits = toCents(getLineTotal(value, values.quantity, warrantyPrice)) <= toCents(remaining);
        return fits || `Only ${formatCurrency(remaining)} left`;
      },
    },
  });
  const totalField = register('total');
  const warrantyField = register('warranty');

  // Same three fields as the tele-sales agent cart: name, price and SKU, all required.
  // Registered only while the box is ticked and unregistered when it's cleared, so an
  // abandoned warranty neither blocks the form nor gets saved.
  const registerWarrantyField = (name, rules) => {
    if (!hasWarranty) return null;
    return register(name, { shouldUnregister: true, ...rules });
  };
  const warrantyNameField = registerWarrantyField('warrantyName', {
    validate: nameRules({ required: 'Enter the warranty name' }),
  });
  const warrantyPriceField = registerWarrantyField('warrantyPrice', {
    validate: value => parsePrice(value) > 0 || 'Enter the warranty price',
    // Counts against the limit together with the item price.
    deps: ['price'],
  });
  const warrantySkuField = registerWarrantyField('warrantySku', {
    validate: {
      required: value => value.trim().length > 0 || 'Enter the warranty SKU',
      characters: value => SKU_CHARACTERS.test(value.trim()) || SKU_CHARACTERS_HINT,
    },
  });

  const onSubmit = values => {
    const warranty = values.warranty && {
      name: values.warrantyName.trim(),
      sku: values.warrantySku.trim(),
      price: roundToCents(parsePrice(values.warrantyPrice)),
    };
    onSave({
      makeModel: values.makeModel.trim(),
      description: values.description.trim(),
      quantity: parseQuantity(values.quantity),
      price: roundToCents(parsePrice(values.price)),
      warranty: warranty || null,
    });
  };

  const renderError = (name, message) => {
    if (!message) return null;
    return (
      <span id={`manual-item-${name}-error`} className="k-item-form__error" role="alert">
        {message}
      </span>
    );
  };

  const makeModelError = errorFor('makeModel');
  const descriptionError = errorFor('description');
  const quantityError = errorFor('quantity');
  const priceError = errorFor('price');
  const warrantyNameError = errorFor('warrantyName');
  const warrantyPriceError = errorFor('warrantyPrice');
  const warrantySkuError = errorFor('warrantySku');

  return (
    <form
      className="k-item-form"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      noValidate
      aria-labelledby="manual-item-title"
    >
      <h4 id="manual-item-title" className="k-item-form__title">
        {title}
      </h4>

      <div className="k-item-form__field">
        <label className="k-item-form__label" htmlFor="manual-item-makeModel">
          Make / Model
        </label>
        <input
          {...makeModelField}
          id="manual-item-makeModel"
          className="k-item-form__input"
          type="text"
          maxLength={60}
          aria-invalid={Boolean(makeModelError)}
          aria-describedby={makeModelError ? 'manual-item-makeModel-error' : undefined}
        />
        {renderError('makeModel', makeModelError)}
      </div>

      <div className="k-item-form__field">
        <label className="k-item-form__label" htmlFor="manual-item-description">
          Item description
        </label>
        <input
          {...descriptionField}
          id="manual-item-description"
          className="k-item-form__input"
          type="text"
          maxLength={120}
          aria-invalid={Boolean(descriptionError)}
          aria-describedby={descriptionError ? 'manual-item-description-error' : undefined}
        />
        {renderError('description', descriptionError)}
      </div>

      <div className="k-item-form__row">
        <div className="k-item-form__field">
          <label className="k-item-form__label" htmlFor="manual-item-quantity">
            Quantity
          </label>
          <input
            {...quantityField}
            id="manual-item-quantity"
            className="k-item-form__input"
            type="text"
            inputMode="numeric"
            onChange={e => {
              e.target.value = sanitizeQuantity(e.target.value);
              quantityField.onChange(e);
              syncTotalFromPrice();
            }}
            aria-invalid={Boolean(quantityError)}
            aria-describedby={quantityError ? 'manual-item-quantity-error' : undefined}
          />
          {renderError('quantity', quantityError)}
        </div>

        <div className="k-item-form__field">
          <label className="k-item-form__label" htmlFor="manual-item-price">
            Item price
          </label>
          <MoneyInput
            field={priceField}
            id="manual-item-price"
            error={priceError}
            onChange={e => {
              e.target.value = sanitizePrice(e.target.value);
              priceField.onChange(e);
              syncTotalFromPrice();
            }}
            onBlur={e => {
              setValue('price', formatPriceInput(e.target.value));
              priceField.onBlur(e);
            }}
          />
          {renderError('price', priceError)}
        </div>

        <div className="k-item-form__field">
          <label className="k-item-form__label" htmlFor="manual-item-total">
            Total price
          </label>
          <MoneyInput
            field={totalField}
            id="manual-item-total"
            onChange={e => {
              e.target.value = sanitizePrice(e.target.value);
              totalField.onChange(e);
              syncPriceFromTotal(e.target.value);
            }}
            onBlur={e => {
              // Settle on what price x quantity actually comes to - e.g. $100 over 3
              // becomes $33.33 each, so the total reads back as $99.99.
              syncTotalFromPrice();
              totalField.onBlur(e);
            }}
          />
        </div>
      </div>

      <label className="k-item-form__checkbox" htmlFor="manual-item-warranty">
        <input
          {...warrantyField}
          id="manual-item-warranty"
          type="checkbox"
          onChange={async e => {
            await warrantyField.onChange(e);
            // `deps` only fires once the checkbox itself has been touched (mode is
            // onTouched), so re-check the limit directly: unticking frees up room.
            trigger('price');
          }}
        />
        <span>Add warranty</span>
      </label>

      {hasWarranty && (
        <fieldset className="k-item-form__warranty">
          <legend className="k-item-form__legend">Warranty details</legend>

          <div className="k-item-form__field">
            <label className="k-item-form__label" htmlFor="manual-item-warrantyName">
              Warranty name
            </label>
            <input
              {...warrantyNameField}
              id="manual-item-warrantyName"
              className="k-item-form__input"
              type="text"
              maxLength={60}
              aria-invalid={Boolean(warrantyNameError)}
              aria-describedby={warrantyNameError ? 'manual-item-warrantyName-error' : undefined}
            />
            {renderError('warrantyName', warrantyNameError)}
          </div>

          <div className="k-item-form__row k-item-form__row--two">
            <div className="k-item-form__field">
              <label className="k-item-form__label" htmlFor="manual-item-warrantyPrice">
                Warranty price
              </label>
              <MoneyInput
                field={warrantyPriceField}
                id="manual-item-warrantyPrice"
                error={warrantyPriceError}
                onChange={e => {
                  e.target.value = sanitizePrice(e.target.value);
                  warrantyPriceField.onChange(e);
                }}
                onBlur={e => {
                  setValue('warrantyPrice', formatPriceInput(e.target.value));
                  warrantyPriceField.onBlur(e);
                }}
              />
              {renderError('warrantyPrice', warrantyPriceError)}
            </div>

            <div className="k-item-form__field">
              <label className="k-item-form__label" htmlFor="manual-item-warrantySku">
                Warranty SKU
              </label>
              <input
                {...warrantySkuField}
                id="manual-item-warrantySku"
                className="k-item-form__input"
                type="text"
                maxLength={30}
                aria-invalid={Boolean(warrantySkuError)}
                aria-describedby={warrantySkuError ? 'manual-item-warrantySku-error' : undefined}
              />
              {renderError('warrantySku', warrantySkuError)}
            </div>
          </div>
        </fieldset>
      )}

      <div className="k-item-form__actions">
        <button type="button" className="k-item-form__button k-item-form__button--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="k-item-form__button k-item-form__button--primary" disabled={!isValid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

MoneyInput.propTypes = {
  field: PropTypes.shape({ name: PropTypes.string, ref: PropTypes.func }).isRequired,
  id: PropTypes.string.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
};

MoneyInput.defaultProps = {
  error: undefined,
};

ManualItemForm.propTypes = {
  remaining: PropTypes.number.isRequired,
  initialValues: PropTypes.shape({
    makeModel: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number,
    warranty: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({ name: PropTypes.string, sku: PropTypes.string, price: PropTypes.number }),
    ]),
  }),
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ManualItemForm.defaultProps = {
  initialValues: undefined,
  title: 'Item details',
  submitLabel: 'Add to cart',
};
